import {
  accessDenialFromClientApiError,
  derivePairedAccessIntakeReadiness,
  type AccessDenialReason,
  type CanonicalPresentationModelV1,
  type ClientJourneyPresentationV1,
} from "@enrein/shared-contracts";

export type ClientNextActionHomeInput = {
  readonly projectId?: string | null;
  readonly clientLinkStatus?: string | null;
  readonly intakeComplete: boolean;
  readonly previewReady: boolean;
  /** Raw API error code before client-safe display mapping, when known. */
  readonly accessErrorCode?: string | null;
  readonly openMissingInformationClarification?: boolean | null;
  /**
   * Server-composed journey presentation (ENR-UX-009). Preferred over coarse
   * Wait-for-preview / Review-and-approve fallbacks for post–Slice-A stages.
   * Client React must never classify run/QA truth from this — only render.
   */
  readonly journeyPresentation?: ClientJourneyPresentationV1 | null;
};

export type ClientNextActionHomeResult = {
  readonly showPlatformMap: false;
  readonly nextActionLabel: string;
  readonly presentation: CanonicalPresentationModelV1 | null;
  readonly outcome: string | null;
};

/**
 * CW-003 one-next-action home — prefer ENR-UX-008 Slice A paired Client presentation
 * when authoritative access/intake inputs are available; then ENR-UX-009 journey
 * presentation for build/QA/preview/review/safe-edit; fall back to coarse labels.
 */
export function resolveClientNextActionHome(input: ClientNextActionHomeInput): ClientNextActionHomeResult {
  const denial: AccessDenialReason | null = input.accessErrorCode
    ? accessDenialFromClientApiError(input.accessErrorCode)
    : null;

  const projectId = input.projectId?.trim() || "client_link_session";
  const paired = derivePairedAccessIntakeReadiness({
    projectId,
    accessDenial: denial,
    clientLink: input.clientLinkStatus
      ? { status: input.clientLinkStatus, accessReady: true }
      : denial
        ? { status: "EXPIRED" }
        : null,
    openMissingInformationClarification: input.openMissingInformationClarification ?? false,
    projectExists: true,
  });

  if (paired) {
    const label =
      paired.client.primaryAction?.label?.trim() ||
      paired.client.title.trim() ||
      "Continue";
    return {
      showPlatformMap: false,
      nextActionLabel: label,
      presentation: paired.client,
      outcome: paired.outcome,
    };
  }

  // ENR-UX-009: prefer server-derived journey model over coarse post–Slice-A labels.
  if (input.journeyPresentation?.presentation) {
    const presentation = input.journeyPresentation.presentation;
    const label =
      presentation.primaryAction?.label?.trim() ||
      presentation.title.trim() ||
      "Continue";
    return {
      showPlatformMap: false,
      nextActionLabel: label,
      presentation,
      outcome: input.journeyPresentation.outcome,
    };
  }

  // Client-submitted without Agency READY gates (ENR-UX-006) — waiting, not coarse preview CTA.
  if (input.clientLinkStatus === "INTAKE_SUBMITTED") {
    return {
      showPlatformMap: false,
      nextActionLabel: "Waiting for your agency",
      presentation: null,
      outcome: "intake_submitted",
    };
  }

  // Coarse fallback for post-Slice-A preview/approval states (out of Slice A matrix).
  if (!input.intakeComplete) {
    return {
      showPlatformMap: false,
      nextActionLabel: "Complete your intake",
      presentation: null,
      outcome: null,
    };
  }
  if (!input.previewReady) {
    return {
      showPlatformMap: false,
      nextActionLabel: "Wait for preview",
      presentation: null,
      outcome: null,
    };
  }
  return {
    showPlatformMap: false,
    nextActionLabel: "Review and approve your site",
    presentation: null,
    outcome: null,
  };
}
